import React from "react";
import styled from "styled-components";
import Modal from "../modal/Modal";
import ProgressBar from "../progress-bar/ProgressBar";
import { BulkDownloadState } from "../../hooks/useBulkDownload";

interface BulkDownloadProgressModalProps {
  state: BulkDownloadState;
  // 取得中のタイトルに表示するダウンロード名（フォルダ名や zip 名）
  downloadName: string;
  onClose: () => void;
}

// タイトル行（アイコン + テキスト）
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .material-symbols-rounded {
    font-size: 24px;
  }
`;

const FolderIcon = styled.span`
  color: var(--soremo-light-gray);
`;

const StatusIcon = styled.span`
  color: var(--soremo-gray);
`;

// プログレスバーと進捗テキストの縦並び
const ProgressArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ProgressText = styled.span.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: var(--soremo-gray);
  text-align: right;
`;

/**
 * zip 一括ダウンロードの保存方法の選択・進捗・結果モーダル。
 *
 * 保存方法の選択（choose）は「ZIP で保存」「フォルダに保存」の 2 択を表示する。
 * 取得中（running）はプログレスバーと件数を表示し、閉じる操作を無効化する。
 * 一部失敗（partial）・失敗（error）では結果と「閉じる」ボタンを表示する。
 * 全件成功時はこのモーダルを表示せず、呼び出し側で自動的に閉じる。
 */
const BulkDownloadProgressModal: React.FC<BulkDownloadProgressModalProps> = ({
  state,
  downloadName,
  onClose,
}) => {
  if (state.phase === "choose") {
    return (
      <Modal
        onClose={onClose}
        size="default"
        titleSize="medium"
        title={
          <TitleRow>
            <FolderIcon className="material-symbols-rounded">folder</FolderIcon>
            <span>{downloadName}</span>
          </TitleRow>
        }
        description="容量が大きいため、保存方法を選んでください。「フォルダに保存」は途中で止まっても、同じ保存先を選んでもう一度実行すると続きから再開できます。"
        descriptionTone="subtle"
        secondaryAction={{ label: "ZIP で保存", theme: "tertiary", onClick: state.chooseZip }}
        primaryAction={{ label: "フォルダに保存", theme: "primary", onClick: state.chooseFolder }}
      />
    );
  }

  const isRunning = state.phase === "running";
  const resumeHint =
    state.mode === "folder"
      ? "同じ保存先を選んでもう一度実行すると、残りのファイルだけを取得します。"
      : "";

  const percent =
    state.phase === "running" && state.total > 0
      ? Math.round((state.processed / state.total) * 100)
      : 0;

  const title =
    state.phase === "running" ? (
      <TitleRow>
        <FolderIcon className="material-symbols-rounded">folder</FolderIcon>
        <span>{downloadName}</span>
      </TitleRow>
    ) : state.phase === "partial" ? (
      <TitleRow>
        <StatusIcon className="material-symbols-rounded">warning</StatusIcon>
        <span>一部をダウンロードしました</span>
      </TitleRow>
    ) : (
      <TitleRow>
        <StatusIcon className="material-symbols-rounded">error</StatusIcon>
        <span>ダウンロード失敗</span>
      </TitleRow>
    );

  const description =
    state.phase === "running"
      ? state.reconnecting
        ? "通信が途切れたため、再接続しています…"
        : undefined
      : state.phase === "partial"
        ? `${state.total}件中${state.succeeded}件をダウンロードしました。${resumeHint}`
        : `ファイルのダウンロードに失敗しました。時間をおいて再度お試しください。${resumeHint}`;

  return (
    <Modal
      onClose={isRunning ? undefined : onClose}
      size="default"
      titleSize="medium"
      title={title}
      description={description}
      descriptionTone="subtle"
      primaryAction={isRunning ? undefined : { label: "閉じる", onClick: onClose }}
    >
      {state.phase === "running" && (
        <ProgressArea>
          <ProgressBar value={percent} height={8} />
          <ProgressText>
            {percent}%（{state.processed}/{state.total}）
          </ProgressText>
        </ProgressArea>
      )}
    </Modal>
  );
};

export default BulkDownloadProgressModal;
